/**
 * Italian / EUR presentation helpers. Pure and client-safe: they format values, they never do
 * financial arithmetic. Amounts arrive as exact decimal strings (Postgres numeric).
 */
import Decimal from "decimal.js";

const MONTHS_IT = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
] as const;

const eur = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "1234.5" → "1234,50 €" (Italian CLDR groups thousands only from 5 digits: "12.345,00 €"). */
export function formatEur(amount: string | Decimal): string {
  const rounded = new Decimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  return eur.format(rounded.toNumber());
}

function monthParts(accountingMonth: string): [number, number] {
  const m = /^(\d{4})-(\d{2})/.exec(accountingMonth);
  if (!m) throw new Error(`Invalid month: ${accountingMonth}`);
  return [Number(m[1]), Number(m[2])];
}

/** "2026-10" or "2026-10-01" → "ottobre 2026". */
export function formatMonth(accountingMonth: string): string {
  const [year, month] = monthParts(accountingMonth);
  return `${MONTHS_IT[month - 1]} ${year}`;
}

/** "2026-10" → "Ottobre 2026" (for headings). */
export function formatMonthTitle(accountingMonth: string): string {
  const text = formatMonth(accountingMonth);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Signed amount for balances and savings: "+12,50 €" / "−150,38 €" (true minus sign). */
export function formatEurSigned(amount: string | Decimal): string {
  const d = new Decimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  if (d.isZero()) return formatEur("0");
  return `${d.isNegative() ? "−" : "+"}${formatEur(d.abs())}`;
}

/** Negative amounts with a true minus sign, positives unsigned: "−150,38 €". */
export function formatEurPlain(amount: string | Decimal): string {
  const d = new Decimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  return d.isNegative() ? `−${formatEur(d.abs())}` : formatEur(d);
}

const compact = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1, notation: "compact" });

/** Axis ticks: "0", "1500", "2,5 Mln"… (no currency sign; the axis title carries it). */
export function formatAxisNumber(value: number): string {
  return Math.abs(value) >= 10_000 ? compact.format(value) : new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(value);
}

/** Ratio "0.0649" → "6,5 %". */
export function formatPercent(ratio: string | Decimal | null, digits = 1): string {
  if (ratio === null) return "—";
  const d = new Decimal(ratio).times(100).toDecimalPlaces(digits, Decimal.ROUND_HALF_UP);
  const body = new Intl.NumberFormat("it-IT", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(d.abs().toNumber());
  return `${d.isNegative() ? "−" : ""}${body} %`;
}

/** "2026-10" → "ott" (chart axes). */
export function formatMonthShort(accountingMonth: string): string {
  const [, month] = monthParts(accountingMonth);
  return MONTHS_IT[month - 1]!.slice(0, 3);
}

/** "2026-09-28" → "28/09/2026". */
export function formatDate(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) throw new Error(`Invalid date: ${isoDate}`);
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** File sizes for the UI: "850 KB", "1,4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("it-IT", { maximumFractionDigits: 1 })} MB`;
}
